/**
 * VINPOP: the only login popup on the site (2026-10-13).
 *
 * One card for every logged-out case: rating a wine, saving a pairing or a playlist wine,
 * the Smart Box demo, the Rate your wines examples, and the private wine-tool collections.
 * The visual is the former layout gate: lock icon, title, sentence, "Create my account" and
 * "I already have an account". The Smart Box inline form and the old emoji modal are retired
 * (archive/theme/2026-10-13-popup-login-unifiee/).
 *
 * Public API:
 *   VinPopLoginPrompt.isLoggedIn()        -> boolean (customer meta)
 *   VinPopLoginPrompt.show(opts)          -> opens the card
 *   VinPopLoginPrompt.requireLogin(opts)  -> Promise<boolean>; shows the card when logged out
 *   VinPopLoginPrompt.close()
 * opts: context (rating | pairing | playlist | smartbox | preview | tools), returnTo, source,
 *       dismissable (false for the gated collections), extraLink { href }.
 *
 * Loaded from layout/theme.liquid (global, deferred, before its consumers). CSS:
 * vinpop-login-required.css. The heading drop-shadow class comes from data-heading-style
 * on the script tag (theme setting heading_drop_shadow_style).
 */
(function () {
  'use strict';

  var script = document.currentScript;
  var headingStyle = (script && script.getAttribute('data-heading-style')) || '';
  var isNL = String(document.documentElement.lang || '').toLowerCase().split('-')[0] === 'nl';
  function tr(en, nl) { return isNL ? nl : en; }

  var t = {
    register: tr('Create my account', 'Account aanmaken'),
    login: tr('I already have an account', 'Ik heb al een account'),
    close: tr('Close', 'Sluiten'),
    continueTitle: tr('Log in to continue', 'Log in om verder te gaan')
  };

  var contexts = {
    rating: {
      title: tr('Log in to rate this wine', 'Log in om deze wijn te beoordelen'),
      body: tr('Your ratings build your taste profile, so we keep them on your account.',
        'Je beoordelingen bouwen jouw smaakprofiel op, daarom bewaren we ze op je account.')
    },
    pairing: {
      title: tr('Log in to save your pairings', 'Log in om je combinaties op te slaan'),
      body: tr('Food pairings are saved on your account, next to your ratings.',
        'Combinaties met eten worden op je account bewaard, naast je beoordelingen.')
    },
    playlist: {
      title: tr('Log in to save this wine', 'Log in om deze wijn te bewaren'),
      body: tr('Your playlist lives on your account, so you find your wines back on any device.',
        'Je playlist staat op je account, zo vind je je wijnen terug op elk apparaat.')
    },
    smartbox: {
      title: tr('Log in to see your personal box', 'Log in om je persoonlijke box te bekijken'),
      body: tr('We build it from the wines you rated in your account.',
        'We stellen hem samen op basis van de wijnen die je in je account hebt beoordeeld.'),
      extra: tr('No wines rated yet? Start with the Tasting Kit', 'Nog geen wijnen beoordeeld? Start met de Tasting Kit')
    },
    preview: {
      title: t.continueTitle,
      body: tr('Log in to see and rate your own wines. The bottles shown are only an example.',
        'Log in om je eigen wijnen te bekijken en te beoordelen. De getoonde flessen zijn alleen een voorbeeld.')
    },
    tools: {
      title: t.continueTitle,
      body: tr('Your personalised wine tools live in your free account. Creating one takes 30 seconds.',
        'Je gepersonaliseerde wijntools staan in je gratis account. Aanmaken duurt 30 seconden.')
    }
  };

  var LOCK = '<svg class="vp-gate__icon text-scheme-accent-2" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>';

  function metaContent(name) {
    var meta = document.querySelector('meta[name="' + name + '"]');
    return meta ? String(meta.getAttribute('content') || '').trim() : '';
  }

  function isLoggedIn() {
    return !!metaContent('customer-id');
  }

  // Cached HTML can miss the customer meta while the Shopify session is valid.
  function hasValidShopifySession() {
    if (isLoggedIn()) return Promise.resolve(true);
    return fetch('/account', { method: 'GET', credentials: 'same-origin', redirect: 'follow', cache: 'no-store' })
      .then(function (response) {
        var finalUrl = (response && response.url) || '';
        if (response.ok && !/\/account\/login(?:[/?#]|$)/.test(finalUrl)) {
          window.location.reload();
          return true;
        }
        return false;
      })
      .catch(function () { return false; });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function button(href, label, surfaceClass) {
    var link = el('a', 'push-btn w-full');
    link.href = href;
    link.appendChild(el('span', 'push-btn__surface w-full ' + surfaceClass, label));
    return link;
  }

  var overlay = null;
  var lastFocused = null;
  var dismissable = true;

  function close() {
    if (!overlay || !dismissable) return;
    overlay.parentNode && overlay.parentNode.removeChild(overlay);
    overlay = null;
    document.documentElement.classList.remove('vp-gate-open');
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function onKeydown(event) {
    if (!overlay) return;
    if (event.key === 'Escape') close();
    if (event.key !== 'Tab') return;
    var controls = overlay.querySelectorAll('button, a[href]');
    var first = controls[0];
    var last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
  document.addEventListener('keydown', onKeydown);

  function show(options) {
    var opts = options || {};
    var preset = contexts[opts.context] || contexts.rating;
    if (overlay) {
      if (!dismissable) return;
      close();
    }
    dismissable = opts.dismissable !== false;
    lastFocused = document.activeElement;

    var query = '?return_to=' + encodeURIComponent(opts.returnTo || (window.location.pathname + window.location.search));
    if (opts.source) query += '&source=' + encodeURIComponent(opts.source);

    overlay = el('div', dismissable ? 'vp-gate vp-gate--modal' : 'vp-gate');
    overlay.setAttribute('data-color-scheme', 'scheme4');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'vp-gate-title');

    var card = el('div', 'vp-gate__card card relative z-shape-divider mx-auto bg-scheme-background text-scheme-text');
    var surface = el('div', 'card__surface bg-scheme-card p-5 lg:p-10 space-y-5 text-center');

    if (dismissable) {
      var closeBtn = el('button', 'vp-gate__close', '\u00d7');
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', t.close);
      closeBtn.addEventListener('click', close);
      surface.appendChild(closeBtn);
    }
    surface.insertAdjacentHTML('beforeend', LOCK);
    var title = el('h2', 'font-heading heading-standard heading-style ' + headingStyle, opts.title || preset.title);
    title.id = 'vp-gate-title';
    surface.appendChild(title);
    surface.appendChild(el('p', 'font-main leading-snug', opts.body || preset.body));

    var actions = el('div', 'grid gap-5');
    actions.appendChild(button('/account/register' + query, t.register, 'bg-scheme-accent-2 text-scheme-accent-2-contrast'));
    var loginLink = button('/account/login' + query, t.login, 'push-btn__surface--secondary');
    actions.appendChild(loginLink);
    surface.appendChild(actions);

    if (opts.extraLink && preset.extra) {
      var extra = el('a', 'vp-gate__extra', preset.extra);
      extra.href = opts.extraLink.href;
      surface.appendChild(extra);
    }

    card.appendChild(surface);
    overlay.appendChild(card);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });
    document.body.appendChild(overlay);
    document.documentElement.classList.add('vp-gate-open');
    if (dismissable) loginLink.focus();
  }

  function requireLogin(options) {
    if (isLoggedIn()) return Promise.resolve(true);
    return hasValidShopifySession().then(function (hasSession) {
      if (hasSession) return true;
      show(options);
      return false;
    });
  }

  window.VinPopLoginPrompt = {
    isLoggedIn: isLoggedIn,
    hasValidShopifySession: hasValidShopifySession,
    requireLogin: requireLogin,
    show: show,
    close: close
  };
})();
